import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { AssetSubClassCode, Entity, isEtf } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useRelatedCompanies } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { About } from '~/modules/about/About';
import { AssetInsightsList } from '~/modules/asset-insights-list/AssetInsightsList';
import { OverviewFinancials } from '~/modules/overview-financials/OverviewFinancials';
import { OverviewKeyValues } from '~/modules/overview-key-values/OverviewKeyValues';
import { OverviewKnowledgeGraph } from '~/modules/overview-knowledge-graph/OverviewKnowledgeGraph';
import { OverviewValuation } from '~/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { sortElementByEdgeWeight } from '~/utils/common/utils';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils.ts/chart-settings-utils';

import * as S from './Overview.styles';

export interface OverviewProps {
  entity: Entity;
}

export const Overview = ({ entity }: OverviewProps) => {
  const { t } = useTranslation(['widget', 'common']);
  const navigateTerminal = useTerminalNavigate();
  const chartNavigate = useChartNavigate();

  const {
    data: relatedCompaniesData,
    isLoading,
    isError,
  } = useRelatedCompanies(entity.tag);

  const competitors = sortElementByEdgeWeight(
    relatedCompaniesData?.competitors ?? []
  );
  const suppliers = sortElementByEdgeWeight(
    relatedCompaniesData?.suppliers ?? []
  );

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const entityIsEtf = isEtf(entity);
  const displayAbout = isStock || entityIsEtf;

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    navigateTerminal({
      navigateFn: () =>
        chartNavigate([entityValue], {
          resample: chartState.resample,
          horizon: chartState.horizon,
          seriesType: chartState.chartPanes[0].seriesType,
        }),
      crumb: {
        label: t('common:sideNavigation.chart'),
        isAccessibleFromSideBar: true,
      },
    });
  };

  return (
    <S.OverviewRoot>
      <SimpleChart
        entity={entity}
        key={entity.tag}
        initialChartSettings={chartSettings}
        isLegacyVariant={false}
        onClickFullscreen={navigateToChart}
      />
      <OverviewKeyValues entityTag={entity.tag} />

      <S.Flex>
        <S.SplitSection>
          {displayAbout && <About entity={entity} />}
        </S.SplitSection>
        {isStock && (
          <S.SplitSection>
            <OverviewKnowledgeGraph
              entityTag={entity.tag}
              isEtf={entityIsEtf}
              companiesData={{
                competitors: relatedCompaniesData?.competitors ?? [],
                suppliers,
                isLoading,
                isError,
              }}
            />
          </S.SplitSection>
        )}
      </S.Flex>
      {isStock && (
        <>
          <OverviewFinancials
            entity={entity}
            competitorsData={{
              competitors,
              isLoading,
            }}
          />
          <OverviewValuation
            entity={entity}
            competitorsData={{
              competitors,
              isLoading,
            }}
          />
        </>
      )}

      <S.Flex>
        <S.SplitSection>
          <AssetInsightsList
            title={
              <SectionTitle
                title={t('widget:assetOverview.insightSectionTitle')}
              />
            }
            entityTag={entity.tag}
          />
        </S.SplitSection>
        <S.SplitSection />
      </S.Flex>
    </S.OverviewRoot>
  );
};
